/**
 * Branded entry pages for insurers: /start/fair-plan, /start/summit-ridge.
 *
 * One link per insurer works for every policyholder. The visitor lands on a
 * page branded for the insurer, searches for their own home on the map, and
 * sees that insurer's checklist for it. The checklist is named by `modelId`
 * (an insuranceChecklists doc id); the brand mark comes from that document.
 *
 * THE COPY IN THIS FILE IS A FIRST DRAFT. Glenn edits it, and the insurer
 * signs it off. It makes no promise about discounts or coverage. The page
 * reads every field from these objects, so a copy change is an edit here.
 */

export interface InsurerPage {
  /** insuranceChecklists doc id the page opens with. */
  modelId: string;
  eyebrow: string;
  headline: string;
  lede: string;
  /** Exactly three. */
  points: [string, string, string];
  cta: { label: string };
}

/** Short heads for the three points, shared by every insurer page. */
export const INSURER_POINT_TITLES = [
  'Search your address',
  'Work the list',
  'Save and share',
] as const;

export const INSURER_PAGES: Record<string, InsurerPage> = {
  'fair-plan': {
    modelId: 'fairplan-homeowner',
    eyebrow: 'For California FAIR Plan policyholders',
    headline: 'Check your home against the California FAIR Plan wildfire checklist.',
    lede: "Find your address on the map and see the California FAIR Plan checklist for your home. Check off what you've already done.",
    points: [
      'Search for your address and open your home.',
      "Check off the steps you've finished and add photos as you go.",
      "Save your progress and share it when you're ready.",
    ],
    cta: { label: 'Find your home' },
  },
  'state-farm': {
    modelId: 'state-farm-homeowner',
    eyebrow: 'For State Farm policyholders',
    headline: 'Check your home against the State Farm wildfire checklist.',
    lede: "Find your address on the map and see the State Farm checklist for your home. Check off what you've already done.",
    points: [
      'Search for your address and open your home.',
      "Check off the steps you've finished and add photos as you go.",
      "Save your progress and share it when you're ready.",
    ],
    cta: { label: 'Find your home' },
  },
  'summit-ridge': {
    modelId: 'summit-ridge-homeowner',
    eyebrow: 'For Summit Ridge Mutual policyholders',
    headline: 'Check your home against the Summit Ridge Mutual wildfire checklist.',
    lede: "Find your address on the map and see the Summit Ridge Mutual checklist for your home. Check off what you've already done.",
    points: [
      'Search for your address and open your home.',
      "Check off the steps you've finished and add photos as you go.",
      "Save your progress and share it when you're ready.",
    ],
    cta: { label: 'Find your home' },
  },
};

/** Resolve a URL segment to an insurer page, or undefined. */
export function insurerPageBySlug(slug: string | undefined): InsurerPage | undefined {
  if (!slug) return undefined;
  const key = slug.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(INSURER_PAGES, key) ? INSURER_PAGES[key] : undefined;
}
